"use strict";
/**
 * 前端重试装饰器
 * 提供方法级别的自动重试能力
 *
 * 支持 TypeScript 5 的新装饰器语法
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Retry = Retry;
exports.exponentialBackoff = exponentialBackoff;
/**
 * 计算延迟时间
 */
function getDelay(delay, attempt) {
    return typeof delay === 'function' ? delay(attempt) : delay;
}
/**
 * 睡眠指定时间
 */
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * 重试装饰器
 *
 * 用于标记需要自动重试的异步方法
 *
 * @example
 * ```typescript
 * class MyApi extends BaseApiClient {
 *   @Retry({ when: isApiServerError, maxAttempts: 3 })
 *   async getList() {
 *     return this.get<ListData>('list');
 *   }
 *
 *   // 使用指数退避
 *   @Retry({
 *     when: isApiRetryable,
 *     maxAttempts: 3,
 *     delay: (attempt) => 1000 * Math.pow(2, attempt - 1),
 *   })
 *   async fetchData() {
 *     return this.get<Data>('data');
 *   }
 * }
 * ```
 */
function Retry(options) {
    const { when, maxAttempts = 3, delay = 1000, onRetry } = options;
    return function (originalMethod, context) {
        const methodName = String(context.name);
        const replacementMethod = async function (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...args) {
            let lastError;
            for (let attempt = 0; attempt <= maxAttempts; attempt++) {
                try {
                    return await originalMethod.call(this, ...args);
                }
                catch (error) {
                    lastError = error;
                    // 检查是否应该重试
                    if (attempt < maxAttempts && when(error)) {
                        onRetry?.(error, attempt + 1);
                        console.debug(`[Retry] ${methodName} 第 ${attempt + 1} 次重试`, error);
                        const delayMs = getDelay(delay, attempt + 1);
                        await sleep(delayMs);
                        continue;
                    }
                    // 不重试，直接抛出
                    throw error;
                }
            }
            // 重试耗尽
            throw lastError;
        };
        return replacementMethod;
    };
}
/**
 * 指数退避延迟函数工厂
 *
 * @param baseDelay 基础延迟（毫秒）
 * @param maxDelay 最大延迟（毫秒）
 * @returns 延迟计算函数
 *
 * @example
 * ```typescript
 * @Retry({
 *   when: isApiServerError,
 *   delay: exponentialBackoff(1000, 10000),
 * })
 * ```
 */
function exponentialBackoff(baseDelay, maxDelay) {
    return (attempt) => {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        return Math.min(delay, maxDelay);
    };
}
